import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const COLOR_RE = /(?:#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)|\b(?:black|white)\b)/;

function isEnabled() {
  if (typeof window === "undefined" || process.env.NODE_ENV === "production") {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.has("themeAudit") || window.localStorage.getItem("atenoThemeAudit") === "1";
}

function findIssueNodes() {
  const nodes = [];

  document.querySelectorAll("[style]").forEach((el) => {
    const styleText = el.getAttribute("style") || "";
    if (COLOR_RE.test(styleText) || /backdropFilter\s*:\s*["']none["']/.test(styleText)) {
      nodes.push({
        element: el,
        tag: el.tagName.toLowerCase(),
        className: el.className?.toString?.() || "",
        style: styleText,
      });
    }
  });

  return nodes;
}

export default function ThemeAuditOverlay() {
  const location = useLocation();
  const [issues, setIssues] = useState([]);

  const enabled = useMemo(() => isEnabled(), []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const scan = () => {
      const nodes = findIssueNodes();

      document.querySelectorAll("[data-theme-audit-issue='true']").forEach((el) => {
        el.removeAttribute("data-theme-audit-issue");
      });

      nodes.forEach(({ element }) => {
        element.setAttribute("data-theme-audit-issue", "true");
      });

      setIssues(nodes.slice(0, 4));
    };

    scan();

    const interval = window.setInterval(scan, 1800);
    return () => window.clearInterval(interval);
  }, [enabled, location.pathname, location.search]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handler = (event) => {
      if (event.key === "Escape") {
        window.localStorage.removeItem("atenoThemeAudit");
        window.location.reload();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  return (
    <aside className="theme-audit-overlay" aria-live="polite">
      <h3>Theme audit</h3>
      <p>{issues.length} inline surface(s) flagged. Press Esc to disable the overlay.</p>
      {issues.length > 0 ? (
        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
          {issues.map((issue, index) => (
            <div key={`${issue.tag}-${index}`} style={{ fontSize: 11, lineHeight: 1.45, color: "var(--text-secondary)" }}>
              <strong style={{ color: "var(--text-primary)" }}>{issue.tag}</strong>
              {issue.className ? <span> .{issue.className.toString().split(" ").filter(Boolean).slice(0, 3).join(".")}</span> : null}
              <div style={{ marginTop: 3, opacity: .85 }}>{issue.style.slice(0, 120)}{issue.style.length > 120 ? "..." : ""}</div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ marginTop: 8 }}>No inline color leaks detected yet.</p>
      )}
    </aside>
  );
}
